import axios, { AxiosInstance, AxiosError } from 'axios';
import { observability } from '@src/config/observability';
import { HttpStatusCode, ApiError } from '@src/shared/models';
import { useAppStore } from '@src/shared/zustand';
import { setToken } from '@src/shared/zustand/';

// Função para construir URL da API
export const makeApiUrl = (path: string): string =>
  `${import.meta.env.VITE_OMNI_BASE_URL}${path}`;

// Criação da instância do axios com configurações base
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_OMNI_BASE_URL,
    timeout: 60000, // 60 segundos
  });

  // Interceptor de requisição para adicionar token automaticamente
  instance.interceptors.request.use(
    (config) => {
      const { token } = useAppStore.getState();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Add timestamp for duration tracking
      (config as any).metadata = { startTime: Date.now() };
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor de resposta para logging e padronização de erros
  instance.interceptors.response.use(
    (response) => {
      // Log successful API calls to Bugsnag
      const config = response.config as any;
      const duration = config.metadata?.startTime
        ? Date.now() - config.metadata.startTime
        : undefined;

      observability.logApiCall({
        method: response.config.method || 'get',
        url: response.config.url || '',
        status: response.status,
        duration,
        requestData: response.config.data,
        responseData: response.data,
      });

      return response;
    },
    (error: AxiosError) => {
      const response = error?.response;
      const config = error.config as any;
      const duration = config?.metadata?.startTime
        ? Date.now() - config.metadata.startTime
        : undefined;

      // Log failed API calls to Bugsnag
      observability.logApiCall({
        method: error.config?.method || 'unknown',
        url: error.config?.url || '',
        status: response?.status || 0,
        duration,
        requestData: error.config?.data,
        responseData: response?.data,
      });

      // Cria um erro customizado com informações estruturadas
      const apiError: ApiError = Object.assign(
        new Error(
          (response?.data as any)?.message ||
            error.message ||
            'Erro na requisição'
        ),
        {
          error: (response?.data as any)?.error,
          message: (response?.data as any)?.message,
          errorCode: (response?.data as any)?.errorCode,
          statusCode: response?.status ?? HttpStatusCode.serverError,
          originalError: error,
        }
      );

      // Report API error to Bugsnag with structured data
      observability.error(apiError);

      // Rejeita a Promise para que possa ser capturada no try/catch
      return Promise.reject(apiError);
    }
  );

  return instance;
};

// Instância singleton do axios
const axiosInstance = createAxiosInstance();

// Funções de API tipadas
export const apiClient = {
  get: async <TResponse>(endpoint: string): Promise<TResponse> => {
    const response = await axiosInstance.get<TResponse>(endpoint);
    return response.data;
  },

  post: async <TResponse, TBody = unknown>(
    endpoint: string,
    data?: TBody
  ): Promise<TResponse> => {
    const response = await axiosInstance.post<TResponse>(endpoint, data);
    return response.data;
  },

  put: async <TResponse, TBody = unknown>(
    endpoint: string,
    data: TBody
  ): Promise<TResponse> => {
    const response = await axiosInstance.put<TResponse>(endpoint, data);
    return response.data;
  },

  delete: async <TResponse>(endpoint: string): Promise<TResponse> => {
    const response = await axiosInstance.delete<TResponse>(endpoint);
    return response.data;
  },
} as const;

// Função utilitária para configurar token manualmente
export const setAuthToken = (token: string) => {
  setToken(token);
};

// Função utilitária para limpar token
export const clearAuthToken = () => {
  setToken(null);
};
